/* eslint-disable */
import { lazy } from 'react';
import { USER_ROLE } from 'constants.js';
import { DEFAULT_PATHS } from 'config.js';

const admin = {
  login: lazy(() => import('views/admin/Login')),
  dashboard: lazy(() => import('views/admin/Dashboard')),
  users: lazy(() => import('views/admin/Users')),
  register: lazy(() => import('views/admin/Register')),
};

const defaultPages = {
  login: lazy(() => import('views/default/Login')),
  register: lazy(() => import('views/default/RegisterEnhanced')),
  forgotPassword: lazy(() => import('views/default/ForgotPassword')),
  resetPassword: lazy(() => import('views/default/ResetPassword')),
};

const landing = {
  home: lazy(() => import('views/landing/Home')),
  about: lazy(() => import('views/landing/About')),
  services: lazy(() => import('views/landing/Services')),
  howItWorks: lazy(() => import('views/landing/HowItWorks')),
  news: lazy(() => import('views/landing/News')),
  contact: lazy(() => import('views/landing/Contact')),
};

const blog = lazy(() => import('views/blog/Blog'));
const community = {
  index: lazy(() => import('views/community/Community')),
  list: lazy(() => import('views/community/CommunityList')),
};
const upgrade = lazy(() => import('views/upgrade/Upgrade'));

const dashboard = {
  gettingStarted: lazy(() => import('views/dashboard/DashboardGettingStarted')),
  analysis: lazy(() => import('views/dashboard/DashboardAnalysis')),
};
const services = {
  database: lazy(() => import('views/services/ServicesDatabase')),
  databaseAdd: lazy(() => import('views/services/ServicesDatabaseAdd')),
  databaseDetail: lazy(() => import('views/services/ServicesDatabaseDetail')),
  storage: lazy(() => import('views/services/ServicesStorage')),
  storageFolder: lazy(() => import('views/services/ServicesStorageFolder')),
  hosting: lazy(() => import('views/services/ServicesHosting')),
  users: lazy(() => import('views/services/ServicesUsers')),
};
const account = {
  settings: lazy(() => import('views/account/AccountSettings')),
  billing: lazy(() => import('views/account/AccountBilling')),
  security: lazy(() => import('views/account/AccountSecurity')),
};
const support = {
  docs: lazy(() => import('views/support/SupportDocs')),
  docsDetail: lazy(() => import('views/support/SupportDocsDetail')),
  knowledgeBase: lazy(() => import('views/support/SupportKnowledgeBase')),
  tickets: lazy(() => import('views/support/SupportTickets')),
  ticketsDetail: lazy(() => import('views/support/SupportTicketsDetail')),
};

const provider = {
  dashboard: lazy(() => import('views/provider/ProviderDashboardNew')),
  services: lazy(() => import('views/provider/ProviderServices')),
  subusers: lazy(() => import('views/provider/ProviderSubusers')),
  orders: lazy(() => import('views/provider/ProviderOrders')),
  events: lazy(() => import('views/provider/events/EventsList')),
  createEvent: lazy(() => import('views/provider/events/CreateEvent')),
  eventDetail: lazy(() => import('views/provider/events/EventDetail')),
  editEvent: lazy(() => import('views/provider/events/EditEvent')),
};

const events = {
  listing: lazy(() => import('views/events/EventsListing')),
  details: lazy(() => import('views/events/EventDetails')),
  registration: lazy(() => import('views/events/EventRegistration')),
  bookingConfirmation: lazy(() => import('views/events/BookingConfirmation')),
  paymentComplete: lazy(() => import('views/events/PaymentComplete')),
  myTickets: lazy(() => import('views/events/MyTickets')),
};

const careRequests = {
  create: lazy(() => import('views/care-requests/CreateCareRequest')),
};

const member = {
  dashboard: lazy(() => import('views/member/MemberDashboardNew')),
  profile: lazy(() => import('views/member/MemberProfile')),
  providers: lazy(() => import('views/member/MyProviders')),
  providerProfile: lazy(() => import('views/member/ProviderProfile')),
  profileCompletion: lazy(() => import('views/member/ProfileCompletion')),
  profilePersonal: lazy(() => import('views/member/ProfilePersonal')),
  profileFaith: lazy(() => import('views/member/ProfileFaith')),
  profilePhysical: lazy(() => import('views/member/ProfilePhysical')),
  profileLifestyle: lazy(() => import('views/member/ProfileLifestyle')),
  profileFamily: lazy(() => import('views/member/ProfileFamily')),
  profileCareer: lazy(() => import('views/member/ProfileCareer')),
  profileLocation: lazy(() => import('views/member/ProfileLocation')),
  profilePreferences: lazy(() => import('views/member/ProfilePreferences')),
  profilePhotos: lazy(() => import('views/member/ProfilePhotos')),
  profileVerification: lazy(() => import('views/member/ProfileVerification')),
};

const appRoot = DEFAULT_PATHS.APP.endsWith('/') ? DEFAULT_PATHS.APP.slice(1, DEFAULT_PATHS.APP.length) : DEFAULT_PATHS.APP;

const routesAndMenuItems = {
  mainMenuItems: [
    {
      path: DEFAULT_PATHS.APP,
      exact: true,
      component: landing.home,
      label: 'Home',
    },
    {
      path: `${appRoot}/about`,
      component: landing.about,
      label: 'About',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/services`,
      component: landing.services,
      label: 'Services',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/how-it-works`,
      component: landing.howItWorks,
      label: 'How It Works',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/news`,
      component: landing.news,
      label: 'News',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/contact`,
      component: landing.contact,
      label: 'Contact',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events`,
      exact: true,
      component: events.listing,
      label: 'Events',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events/booking/:bookingNumber`,
      component: events.bookingConfirmation,
      label: 'Booking Confirmation',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events/payment-complete`,
      component: events.paymentComplete,
      label: 'Payment Complete',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events/:slug/register`,
      component: events.registration,
      label: 'Event Registration',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/events/:slug`,
      component: events.details,
      label: 'Event Details',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/my/tickets`,
      component: events.myTickets,
      label: 'My Tickets',
      hideInMenu: true,
      protected: true,
    },
    {
      path: `${appRoot}/login`,
      component: defaultPages.login,
      label: 'Login',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/register`,
      component: defaultPages.register,
      label: 'Register',
      hideInMenu: true,
    },
    {
      path: `${appRoot}/admin/login`,
      component: admin.login,
      label: 'Admin Login',
      hideInMenu: true,
    },
    // {
    //   path: `${appRoot}/blog`,
    //   component: blog,
    //   label: 'menu.blog',
    //   icon: 'file-text',
    //   hideInMenu: true,
    // },
    // {
    //   path: `${appRoot}/upgrade`,
    //   label: 'menu.upgrade',
    //   icon: 'trend-up',
    //   component: upgrade,
    //   hideInMenu: true,
    // },
    // {
    //   path: `${appRoot}/community`,
    //   label: 'menu.community',
    //   icon: 'messages',
    //   component: community.index,
    //   hideInMenu: true,
    //   subs: [
    //     {
    //       path: '/list',
    //       label: 'menu.community-list',
    //       hideInMenu: true,
    //       component: community.list,
    //     },
    //   ],
    // },
  ],
  sidebarItems: [
    // Member/Family Dashboard (First for family members)
    {
      path: `${appRoot}/member/dashboard`,
      label: 'Dashboard',
      icon: 'home',
      exact: true,
      component: member.dashboard,
      protected: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Member Profile (Second for family members)
    {
      path: `${appRoot}/member/profile`,
      label: 'Profile',
      icon: 'user',
      exact: true,
      component: member.profile,
      protected: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // My Providers (Third for family members)
    {
      path: `${appRoot}/member/providers`,
      label: 'menu.my-providers',
      icon: 'people',
      exact: true,
      component: member.providers,
      protected: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Provider Profile (Hidden from menu)
    {
      path: `${appRoot}/member/provider/:providerId`,
      label: 'Provider Profile',
      icon: 'user',
      exact: true,
      component: member.providerProfile,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Completion
    {
      path: `${appRoot}/member/profile-completion`,
      label: 'Complete Profile',
      icon: 'edit',
      exact: true,
      component: member.profileCompletion,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Personal (Hidden from menu)
    {
      path: `${appRoot}/member/profile-completion/personal`,
      label: 'Personal Information',
      icon: 'user',
      exact: true,
      component: member.profilePersonal,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Faith (Hidden from menu)
    {
      path: `${appRoot}/member/profile-completion/faith`,
      label: 'Faith & Spirituality',
      icon: 'church',
      exact: true,
      component: member.profileFaith,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Physical (Hidden from menu)
    {
      path: `${appRoot}/member/profile-completion/physical`,
      label: 'Physical Attributes',
      icon: 'body',
      exact: true,
      component: member.profilePhysical,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Lifestyle (Hidden from menu)
    {
      path: `${appRoot}/member/profile-completion/lifestyle`,
      label: 'Lifestyle',
      icon: 'activity',
      exact: true,
      component: member.profileLifestyle,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Family (Hidden from menu)
    {
      path: `${appRoot}/member/profile-completion/family`,
      label: 'Family & Relationships',
      icon: 'home',
      exact: true,
      component: member.profileFamily,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Career (Hidden from menu)
    {
      path: `${appRoot}/member/profile-completion/career`,
      label: 'Education & Career',
      icon: 'briefcase',
      exact: true,
      component: member.profileCareer,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Location (Hidden from menu)
    {
      path: `${appRoot}/member/profile-completion/location`,
      label: 'Location',
      icon: 'pin',
      exact: true,
      component: member.profileLocation,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Preferences (Hidden from menu)
    {
      path: `${appRoot}/member/profile-completion/preferences`,
      label: 'Dating Preferences',
      icon: 'heart',
      exact: true,
      component: member.profilePreferences,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Photos (Hidden from menu)
    {
      path: `${appRoot}/member/profile-completion/photos`,
      label: 'Profile Photos',
      icon: 'image',
      exact: true,
      component: member.profilePhotos,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Profile Verification (Hidden from menu)
    {
      path: `${appRoot}/member/profile-completion/verification`,
      label: 'Identity Verification',
      icon: 'shield',
      exact: true,
      component: member.profileVerification,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Request Service (Third for family members)
    {
      path: `${appRoot}/service-requests/create`,
      label: 'menu.request-service',
      icon: 'plus',
      exact: true,
      component: careRequests.create,
      protected: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Users (Fourth for family members)
    {
      path: `${appRoot}/users`,
      label: 'menu.users',
      icon: 'user',
      exact: true,
      component: lazy(() => import('views/users/Users')),
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Member, USER_ROLE.FamilyMember, USER_ROLE.Patient],
    },
    // Provider Dashboard (First for providers)
    {
      path: `${appRoot}/provider/dashboard`,
      label: 'Dashboard',
      icon: 'home',
      exact: true,
      component: provider.dashboard,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Services (Second for providers)
    {
      path: `${appRoot}/provider/services`,
      label: 'My Services',
      icon: 'diagram-1',
      exact: true,
      component: provider.services,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Orders (Third for providers)
    {
      path: `${appRoot}/provider/orders`,
      label: 'My Orders',
      icon: 'cart',
      exact: true,
      component: provider.orders,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Subusers (Fourth for providers)
    {
      path: `${appRoot}/provider/subusers`,
      label: 'My Subusers',
      icon: 'user',
      exact: true,
      component: provider.subusers,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // My Events (Fifth for providers)
    {
      path: `${appRoot}/provider/events`,
      label: 'My Events',
      icon: 'calendar',
      exact: true,
      component: provider.events,
      protected: true,
      roles: [USER_ROLE.Provider],
    },
    // Create Event (Hidden from menu)
    {
      path: `${appRoot}/provider/events/create`,
      label: 'Create Event',
      icon: 'plus',
      component: provider.createEvent,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Provider],
    },
    // Edit Event (Hidden from menu)
    {
      path: `${appRoot}/provider/events/:id/edit`,
      label: 'Edit Event',
      icon: 'edit',
      component: provider.editEvent,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Provider],
    },
    // Event Detail (Hidden from menu)
    {
      path: `${appRoot}/provider/events/:id`,
      label: 'Event Detail',
      icon: 'eye',
      component: provider.eventDetail,
      protected: true,
      hideInMenu: true,
      roles: [USER_ROLE.Provider],
    },
    {
      path: `${appRoot}/dashboard`,
      label: 'menu.dashboard',
      icon: 'grid-1',
      exact: true,
      redirect: true,
      to: `${appRoot}/dashboard/getting-started`,
      hideInMenu: true,
      subs: [
        { path: '/getting-started', label: 'menu.getting-started', icon: 'navigate-diagonal', component: dashboard.gettingStarted },
        { path: '/analysis', label: 'menu.analysis', icon: 'chart-4', component: dashboard.analysis },
      ],
    },
    // Temporarily commented out - will activate as features are developed
    /*
    {
      path: `${appRoot}/services`,
      label: 'menu.services',
      icon: 'grid-1',
      exact: true,
      redirect: true,
      to: `${appRoot}/services/database`,
      subs: [
        {
          path: '/database',
          label: 'menu.database',
          icon: 'database',
          component: services.database,
          subs: [
            { path: '/add', label: 'menu.database-add', hideInMenu: true, component: services.databaseAdd },
            { path: '/detail', label: 'menu.database-detail', hideInMenu: true, component: services.databaseDetail },
          ],
        },
        {
          path: '/storage',
          label: 'menu.storage',
          icon: 'file-image',
          component: services.storage,
          subs: [{ path: '/folder', label: 'menu.storage', hideInMenu: true, component: services.storageFolder }],
        },
        { path: '/storage-folder', component: services.storageFolder, hideInMenu: true },
        { path: '/hosting', label: 'menu.hosting', icon: 'router', component: services.hosting },
        { path: '/users', label: 'menu.users', icon: 'user', component: services.users },
      ],
    },
    */
    // Temporarily commented out - will activate as features are developed
    /*
    {
      path: `${appRoot}/account`,
      label: 'menu.account',
      icon: 'user',
      exact: true,
      redirect: true,
      to: `${appRoot}/account/settings`,
      subs: [
        { path: '/settings', label: 'menu.settings', icon: 'gear', component: account.settings },
        { path: '/billing', label: 'menu.billing', icon: 'credit-card', component: account.billing },
        { path: '/security', label: 'menu.security', icon: 'shield', component: account.security },
      ],
    },
    */
    // Temporarily commented out - will activate as features are developed
    /*
    {
      path: `${appRoot}/support`,
      label: 'menu.support',
      icon: 'help',
      exact: true,
      redirect: true,
      to: `${appRoot}/support/docs`,
      subs: [
        {
          path: '/docs',
          label: 'menu.docs',
          icon: 'file-empty',
          component: support.docs,
          subs: [{ path: '/detail', label: 'menu.docs', icon: 'file-empty', component: support.docsDetail, hideInMenu: true }],
        },
        { path: '/knowledge-base', label: 'menu.knowledge-base', icon: 'notebook-1', component: support.knowledgeBase },
        {
          path: '/tickets',
          label: 'menu.tickets',
          icon: 'bookmark',
          component: support.tickets,
          subs: [{ path: '/detail', label: 'menu.tickets-detail', icon: 'file-empty', component: support.ticketsDetail, hideInMenu: true }],
        },
      ],
    },
    */
  ],
};
export default routesAndMenuItems;
